import type { Product, CatalogFilters, CatalogFilter, CatalogSort, PaginatedProducts, Availability, CatalogProduct, CatalogPage, CatalogEnvelope, CatalogAvailability } from '../types/product';
import { API_CONFIG } from './config';

const LOGO_FALLBACK = 'https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad';

function ensureAbsoluteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // relative URL — prepend API base
  const base = API_CONFIG.BASE_URL.replace(/\/+$/, '');
  const path = url.replace(/^\/+/, '');
  return `${base}/${path}`;
}

// ─── Build query string for InnoFlex catalog API ──────────────────────────────
/**
 * Builds the URL query string following InnoFlex's convention:
 *   page=1&pageSize=12&globalFilter=laptop&allData=false
 *   &filters[0].column=brandId&filters[0].value=3
 *   &sorts[0].sortBy=price&sorts[0].sortDirection=asc
 */
function buildQueryString(filters: CatalogFilters): string {
  const params = new URLSearchParams();

  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  if (filters.globalFilter) params.set('globalFilter', filters.globalFilter);

  // allData defaults to false per InnoFlex contract
  params.set('allData', filters.allData !== undefined ? String(filters.allData) : 'false');

  (filters.filters ?? []).forEach((f: CatalogFilter, i: number) => {
    params.set(`filters[${i}].column`, f.column);
    params.set(`filters[${i}].value`, String(f.value));
  });

  (filters.sorts ?? []).forEach((s: CatalogSort, i: number) => {
    params.set(`sorts[${i}].sortBy`, s.sortBy);
    params.set(`sorts[${i}].sortDirection`, s.sortDirection);
  });

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ─── Normalize InnoFlex CatalogAvailability → internal Availability ────────────
function normalizeAvailability(raw: Partial<CatalogAvailability>): Availability {
  return {
    branchName: String(raw.branchName ?? raw.branch ?? ''),
    quantity: Number(raw.quantity ?? raw.stock ?? 0),
    price: Number(raw.price ?? 0),
    stockId: raw.stockId ?? undefined,
    branchId: raw.branchId ?? undefined,
    warehouseId: raw.warehouseId ?? null,
    warehouseName: raw.warehouseName ?? null,
    stockLocation: raw.stockLocation ?? null,
    primaryIdentifier: raw.primaryIdentifier ?? null,
  };
}

// ─── Normalize InnoFlex CatalogProduct → internal Product ──────────────────────
/**
 * Accepts either a real CatalogProduct from the API or a legacy/mock record.
 * Fallbacks:
 *   - `image` (string) → `images[0]`  (legacy pre-v2 field)
 *   - `code` → `sku`                   (bridge for Cart/Compare contexts)
 */
function normalizeProduct(raw: unknown): Product {
  const item = raw as Partial<CatalogProduct> & Record<string, unknown>;

  const productId = Number(item.productId ?? item.id ?? 0);

  // Availability
  const availability: Availability[] = Array.isArray(item.availability)
    ? (item.availability as unknown[])
        .map((a) => normalizeAvailability(a as Partial<CatalogAvailability>))
    : [];

  const productType = Number(item.productType ?? 0);
  const subCategory = item.productSubCategory != null ? String(item.productSubCategory) : null;
  const subCategoryId = item.productSubCategoryId != null ? Number(item.productSubCategoryId) : undefined;

  // Images: prefer `images[]`; fallback to `media[]` (Supabase), then legacy `image` string
  let images: string[] = [];
  if (Array.isArray(item.images)) {
    images = (item.images as unknown[])
      .map((img) => {
        const rawUrl = typeof img === 'string' ? img : (img as Record<string, unknown>)?.url;
        return typeof rawUrl === 'string' ? getProxiedImageUrl(ensureAbsoluteUrl(rawUrl)) : '';
      })
      .filter((url) => !!url);
  }
  // Supabase media format: [{url, type}]
  if (images.length === 0 && Array.isArray(item.media)) {
    images = (item.media as unknown[])
      .map((m) => {
        const rawUrl = typeof m === 'string' ? m : (m as Record<string, unknown>)?.url;
        return typeof rawUrl === 'string' ? getProxiedImageUrl(ensureAbsoluteUrl(rawUrl)) : '';
      })
      .filter((url) => !!url);
  }
  if (images.length === 0 && typeof item.image === 'string' && item.image) {
    images = [getProxiedImageUrl(ensureAbsoluteUrl(item.image))]; // legacy fallback
  }

  const priceVal = item.price ?? 0;

  return {
    // InnoFlex native
    productId,
    name: String(item.name ?? ''),
    images,
    description: item.description != null ? String(item.description) : null,
    brand: item.brand != null ? String(item.brand) : null,
    productSubCategory: subCategory,
    productSubCategoryId: subCategoryId,
    productType,
    price: Number(priceVal),
    availability,

    // Compatibility bridge
    id: String(productId),
    code: item.code != null ? String(item.code) : undefined,
    sku: item.code != null ? String(item.code) : item.sku != null ? String(item.sku) : undefined,
    category: subCategory ?? '',
    stock: availability.reduce((sum, a) => sum + a.quantity, 0) || undefined,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch paginated products from the real EBM catalog API.
 * Never falls back to mock data — throws on any failure.
 *
 * @example
 * fetchProducts({ page: 1, pageSize: 12, globalFilter: 'laptop' })
 * fetchProducts({ page: 1, pageSize: 12, sorts: [{ sortBy: 'price', sortDirection: 'asc' }] })
 * fetchProducts({ filters: [{ column: 'productSubCategoryId', value: 10 }] })
 */
export async function fetchProducts(
  filters: CatalogFilters = {}
): Promise<PaginatedProducts> {
  const merged: CatalogFilters = {
    page: 1,
    pageSize: API_CONFIG.DEFAULT_PAGE_SIZE,
    ...filters,
  };

  if (!API_CONFIG.BASE_URL) {
    throw new Error('API_BASE_URL no configurado. No se pueden cargar productos.');
  }

  const qs = buildQueryString(merged);
  const ts = `&_t=${Date.now()}`;
  const hasQs = qs.includes('?');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CATALOG(API_CONFIG.COMPANY_ID)}${qs}${hasQs ? ts : `?${ts.slice(1)}`}`;

  const headers: Record<string, string> = API_CONFIG.CATALOG_KEY
    ? { 'X-Catalog-Key': API_CONFIG.CATALOG_KEY }
    : {};

  const res = await fetch(url, { headers, cache: 'no-store' });
  if (!res.ok) throw new Error(`EBM Catalog ${res.status}: ${res.statusText}`);

  const json = await res.json();

  // Response shape: CatalogEnvelope<CatalogPage<CatalogProduct>>
  const envelope = json as CatalogEnvelope<CatalogPage<CatalogProduct>>;
  const pageData = envelope.data ?? (json as unknown as CatalogPage<CatalogProduct>);

  const rawItems: unknown[] = Array.isArray(pageData?.items)
    ? pageData.items
    : Array.isArray(pageData)
    ? pageData
    : [];

  const data = rawItems.map(normalizeProduct);
  const page = Number(pageData?.page ?? merged.page ?? 1);
  const pageSize = Number(pageData?.pageSize ?? merged.pageSize ?? API_CONFIG.DEFAULT_PAGE_SIZE);
  const totalCount = Number(pageData?.totalCount ?? data.length);
  const totalPages = Number(pageData?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize)));

  return {
    data,
    pagination: {
      current_page: page,
      per_page: pageSize,
      total_items: totalCount,
      total_pages: totalPages,
      has_next: page < totalPages,
      has_prev: page > 1,
    },
  };
}

/**
 * Fetch all products flat — used by home page sections and search suggestions.
 * Iterates through ALL pages available on the API until the complete catalog
 * is loaded, then flattens the results into a single array.
 * Never falls back to mock data.
 */
export async function fetchAllProducts(
  filters?: Omit<CatalogFilters, 'page' | 'pageSize'>
): Promise<Product[]> {
  const PAGE_SIZE = 50; // conservative — backends often cap at 24-50

  const first = await fetchProducts({ ...filters, pageSize: PAGE_SIZE, page: 1 });
  let all = [...first.data];
  const totalItems = first.pagination.total_items;
  let totalPages = first.pagination.total_pages;

  // Safety: if the backend caps pageSize but reports totalPages=1
  // (e.g. requested 50 but only got 24, totalCount=100 → totalPages=1
  // because backend uses requested pageSize for totalPages calc),
  // we keep fetching until we have all items.
  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        fetchProducts({ ...filters, pageSize: PAGE_SIZE, page: i + 2 })
      )
    );
    rest.forEach((r) => {
      all = all.concat(r.data);
    });
  } else if (all.length < totalItems) {
    // Backend is capping per-page but totalPages says 1
    let page = 2;
    while (all.length < totalItems) {
      const next = await fetchProducts({ ...filters, pageSize: PAGE_SIZE, page });
      if (next.data.length === 0) break;
      all = all.concat(next.data);
      page++;
      // Safety cap: max 200 pages = 10,000 products
      if (page > 200) break;
    }
  }

  return all;
}

/**
 * Fetch single product by productId — uses direct endpoint GET /api/catalog/{companyId}/{productId}
 * Response shape: CatalogEnvelope<CatalogProduct>
 * Returns null on any failure (caller shows "not found").
 *
 * @param id        - the numeric productId
 * @param companyId - the companyId from the URL (falls back to API_CONFIG.COMPANY_ID)
 */
export async function fetchProductById(
  id: string | number,
  companyId?: string | number
): Promise<Product | null> {
  const cid = String(companyId ?? API_CONFIG.COMPANY_ID);

  if (!API_CONFIG.BASE_URL) {
    console.warn('[SpartechRD] No API_BASE_URL configured — cannot fetch product');
    return null;
  }

  const ts = `_t=${Date.now()}`;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PRODUCT(cid, id)}?${ts}`;

  const headers: Record<string, string> = API_CONFIG.CATALOG_KEY
    ? { 'X-Catalog-Key': API_CONFIG.CATALOG_KEY }
    : {};

  const res = await fetch(url, { headers, cache: 'no-store' });
  if (!res.ok) {
    // 404 is expected for missing products, not an error
    if (res.status === 404) return null;
    throw new Error(`EBM Catalog ${res.status}: ${res.statusText}`);
  }
  const json = await res.json();

  // Shape: CatalogEnvelope<CatalogProduct>
  const envelope = json as CatalogEnvelope<CatalogProduct>;
  const raw = envelope.data ?? (json as unknown as Partial<CatalogProduct>);

  if (!raw || typeof raw !== 'object') return null;
  return normalizeProduct(raw);
}

function getProxiedImageUrl(url: string): string {
  if (!url) return '';
  // S3 pre-signed URLs already contain AWSAccessKeyId + Signature in the query
  // string. They are self-authenticating and do NOT need a proxy. Sending
  // extra headers or proxying through Supabase Edge Functions (which may
  // require JWT) breaks them. Use them directly.
  const isS3Presigned = url.includes('AWSAccessKeyId') || url.includes('Signature=');
  if (isS3Presigned) return url;

  // For non-S3 images that might need auth, proxy through Supabase Edge Function.
  const supabaseUrl = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) || '';
  if (!supabaseUrl) return url;
  const proxyBase = `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/image-proxy`;
  const catalogKey = API_CONFIG.CATALOG_KEY;
  const qs = catalogKey
    ? `?url=${encodeURIComponent(url)}&key=${encodeURIComponent(catalogKey)}`
    : `?url=${encodeURIComponent(url)}`;
  return `${proxyBase}${qs}`;
}